// MainLayout.tsx
import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { Box, CssBaseline, Toolbar } from '@mui/material';
import { logout } from "../../modules/login/reducers";
import Loader from '../common/Loader';
import { RootState, LoginAppDispatch } from '../../store'; // Corrected import
import Navbar from '../Navbar';
import Sidebar from '../Sidebar';
import OrgNavbar from '../OrgNavbar';
import OrgSidebar from '../OrgSidebar';
import FacilitySidebar from '../FacilitySidebar';
import FacilityNavbar from '../FacilityNavbar';
import { drawerWidth, collapsedDrawerWidth } from '../../utils/theme';
import Breadcrumb from '../Breadcrumb';
import { useBreadcrumb } from '../../context/BreadcrumbContext';
import { useSidebar } from '../../context/SidebarContext';
import {
  FACILITY_USERTYPE,
  ORG_USERTYPE,
  ORG_STAFF_USERTYPE,
  SUPERADMIN_USERTYPE,
  selectUserProfile,
  fetchUserPermissions
} from '../../modules/user'
import { fetchFacilityProfile } from '../../modules/facilityProfile';
import { fetchProfile } from '../../modules/orgProfile';

const MainLayout: React.FC = () => {
  const dispatch = useDispatch<LoginAppDispatch>();
  const isLoading = useSelector((state: RootState) => state.loader.isLoading);
  const userProfile = useSelector(selectUserProfile)
  const userType = userProfile?.data?.user_type;

  useEffect(() => {
    const getProfile = (type: string) => {
      switch (type) {
        case FACILITY_USERTYPE:
          return dispatch(fetchFacilityProfile());
        case ORG_USERTYPE:
        case ORG_STAFF_USERTYPE:
        case SUPERADMIN_USERTYPE:
          return dispatch(fetchProfile());
        default:
          return;
      }
    }
    if (userType) {
      getProfile(userType);
      dispatch(fetchUserPermissions());
    }
  }, [userType, dispatch])

  const { path, isShow } = useBreadcrumb();
  const { isCollapsed } = useSidebar();

  const handleLogout = (): void => {
    dispatch(logout());
  }



  return (
    // Use MUI's Box component with display: 'flex' for the main layout container
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* 1. RENDER NAVBAR AND SIDEBAR */}
      {/* These components are fixed/permanent and define their own styling (width, position, z-index) */}

      {userType === SUPERADMIN_USERTYPE &&
        <>
          <Navbar />
          <Sidebar onLogout={handleLogout} />
        </>
      }

      {userType === FACILITY_USERTYPE &&
        <>
          <FacilityNavbar />
          <FacilitySidebar onLogout={handleLogout} />
        </>
      }

      {(userType === ORG_USERTYPE || userType === ORG_STAFF_USERTYPE) &&
        <>
          <OrgNavbar />
          <OrgSidebar onLogout={handleLogout} />
        </>
      }

      {/* 2. MAIN CONTENT AREA */}
      <Box
        component="main"
        sx={{
          flexGrow: 1, // Allows content to take up the remaining space
          p: 3,         // Padding: 3 * 8px = 24px (MUI spacing)
          // Essential style to push the content to the right of the permanent sidebar
          width: { sm: `calc(100% - ${(userType === ORG_USERTYPE || userType === ORG_STAFF_USERTYPE) && isCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          minHeight: '100vh', // Ensure background covers the whole screen height
          backgroundColor: (theme) => theme.palette.background.default,
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >

        {/*
          CRITICAL FIX: The empty <Toolbar /> is required to push the content down
          past the fixed-position Navbar.
        */}
        <Toolbar />

        {/* Your existing components */}
        <Loader open={isLoading} />
        {/* 1. FIXED BREADCRUMB POSITIONED INSIDE MAIN CONTENT AREA */}
        {isShow && <Breadcrumb path={path} />}
        {/* Router Outlet for Page Content */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;